/**
 * Permission Hooks
 * 
 * Custom hooks for checking staff permissions in frontend
 */

import { useMemo } from 'react'
import { usePlayerAuth } from '@/contexts/PlayerAuthContext'
import { StaffPermission, hasPermission, hasAnyPermission, hasAllPermissions } from '@/lib/constants/staff-permissions'

export interface PlayerRole {
  id: string
  name: string
  isStaff: boolean
  permissions: string[]
  userCss?: string
  badgeCss?: string
}

/**
 * Hook to check if the current player has specific permissions
 */
export function usePermissions() {
  const { player } = usePlayerAuth()

  const permissions = useMemo<string[]>(() => {
    if (!player?.role) return []
    return player.role.permissions
  }, [player])

  const isStaff = useMemo<boolean>(() => {
    if (!player?.role) return false
    return player.role.isStaff
  }, [player])

  return {
    permissions,
    isStaff,
    hasPermission: (permission: StaffPermission) => hasPermission(permissions, permission),
    hasAnyPermission: (requiredPermissions: StaffPermission[]) => hasAnyPermission(permissions, requiredPermissions),
    hasAllPermissions: (requiredPermissions: StaffPermission[]) => hasAllPermissions(permissions, requiredPermissions),
  }
}
